/**
 * `retrieve-contract` — FR-18, FR-01…FR-05, FR-09, FR-11, NFR-06.
 *
 * The second authorisation checkpoint on the read path (§2.4.4): role first
 * — *may this role take this action?* — then scope, resolved from Entra group
 * claims and applied as a predicate. An out-of-scope key returns `not_found`, not
 * `forbidden`; see `errors.ts`.
 */
import { z } from 'zod';
import { ContractKeySchema } from '../primitives.js';
import { ContractDetailSchema, ContractSummarySchema, ObligationSchema, } from '../domain/contract.js';
import { ContractClauseSchema } from '../domain/clause.js';
import { NotificationSchema } from '../domain/notification.js';
import { VerificationQueueItemSchema } from '../domain/verification.js';
/**
 * Optional sections. Everything not requested comes back `null` rather than
 * missing, so a caller can tell "not fetched" from "empty".
 *
 * `full_text` is opt-in for a reason: it is the largest field in the system, the
 * front end needs it only when rendering the document view, and it is the field a
 * naive client would refetch on every list render.
 */
export const CONTRACT_INCLUDE_SECTIONS = [
    'obligations',
    'clauses',
    'family',
    'verifications',
    'notifications',
    'full_text',
];
export const ContractIncludeSectionSchema = z.enum(CONTRACT_INCLUDE_SECTIONS);
export const RetrieveContractRequestSchema = z
    .object({
    contract_key: ContractKeySchema,
    include: z.array(ContractIncludeSectionSchema).optional(),
})
    .strict();
/**
 * One member of the agreement family — §8.1, *"show me this agreement's whole
 * history"*.
 *
 * Every member returned has passed the same scope predicate as the contract
 * itself. A family member the caller cannot see is absent from this list, and
 * `family_truncated_by_scope` says so rather than leaving a silent gap in a
 * lineage the user is reading as complete.
 */
export const AgreementFamilyMemberSchema = z.object({
    contract: ContractSummarySchema,
    /** How this member relates to the contract that was requested. */
    relationship: z.enum([
        'self',
        'parent',
        'child',
        'amends',
        'amended_by',
        'supersedes',
        'superseded_by',
        'sibling',
    ]),
});
export const RetrieveContractResponseSchema = z.object({
    contract: ContractDetailSchema,
    /** FR-03. `null` unless `obligations` was requested. */
    obligations: z.array(ObligationSchema).nullable(),
    /** FR-16/FR-28 — clause instances as spans, never as text. */
    clauses: z.array(ContractClauseSchema).nullable(),
    family: z.array(AgreementFamilyMemberSchema).nullable(),
    /** True when at least one family member was excluded by the scope predicate. */
    family_truncated_by_scope: z.boolean(),
    /** FR-07 — the fields on this contract still awaiting verification. */
    verifications: z.array(VerificationQueueItemSchema).nullable(),
    /** §3 — alerts sent for this contract, so the UI can show what is outstanding. */
    notifications: z.array(NotificationSchema).nullable(),
    /**
     * The converted document text (FR-20).
     *
     * Comes from the converter, **never** from a model: output is priced ~5× input
     * and a model re-transcribing twenty pages introduces errors (CLAUDE.md rule
     * 13). Clause `span` offsets index into exactly this string.
     *
     * `null` unless `full_text` was requested. Also `null` where conversion used the
     * `pdf_native_fallback` path — there is no extracted text in that case, which is
     * the same fact that makes the fallback an FR-29 compliance decision.
     */
    full_text: z.string().nullable(),
});
//# sourceMappingURL=retrieve-contract.js.map